import { resolutionsMap } from '@/lib/assets/assets'

const RESOLUTION_TOKEN_TO_KEY: Record<string, keyof typeof resolutionsMap> = {
  uhd: '4k',
  ultra: '4k',
  ultra_hd: '4k',
  ultrahd: '4k',
  fullhd: 'hd',
  full_hd: 'hd',
  fhd: 'hd',
  hdready: 'hd',
  hd_ready: 'hd',
  qhd: '3k',
  wqhd: '3k',
  lq: 'lq',
}

const buildTokenScores = () => {
  const scores: Record<string, number> = {}

  Object.entries(resolutionsMap).forEach(([key, value]) => {
    const area = value.width * value.height
    scores[key] = area
  })

  Object.entries(RESOLUTION_TOKEN_TO_KEY).forEach(([token, key]) => {
    scores[token] = scores[key]
  })

  return scores
}

const RESOLUTION_TOKEN_SCORES = buildTokenScores()

const K_RESOLUTION_KEYS = new Set(Object.keys(resolutionsMap))

const normalizeToken = (token: string) => token.toLowerCase().replace(/[^a-z0-9]/g, '')

export const getResolutionScore = (raw?: string | null): number => {
  if (!raw) {
    return 0
  }

  let bestScore = 0
  const normalized = raw.toLowerCase()

  const dimensionRegex = /(\d+)\s*[x×]\s*(\d+)/gi
  let dimensionMatch: RegExpExecArray | null = null

  while ((dimensionMatch = dimensionRegex.exec(normalized))) {
    const width = Number.parseInt(dimensionMatch[1], 10)
    const height = Number.parseInt(dimensionMatch[2], 10)
    if (Number.isFinite(width) && Number.isFinite(height)) {
      bestScore = Math.max(bestScore, width * height)
    }
  }

  const tokens = normalized.split(/[^a-z0-9.]+/g).filter(Boolean)

  for (const token of tokens) {
    const simpleToken = normalizeToken(token)

    if (simpleToken in RESOLUTION_TOKEN_SCORES) {
      bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES[simpleToken])
      continue
    }

    if (K_RESOLUTION_KEYS.has(simpleToken)) {
      bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES[simpleToken])
      continue
    }

    const kMatch = /^(\d+(?:\.\d+)?)k$/.exec(simpleToken)
    if (kMatch) {
      const numeric = Number.parseFloat(kMatch[1])
      if (Number.isFinite(numeric) && numeric > 0) {
        const rounded = Math.round(numeric)
        const canonicalKey = `${rounded}k`
        if (canonicalKey in RESOLUTION_TOKEN_SCORES) {
          bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES[canonicalKey])
        }
      }
      continue
    }

    const pMatch = /^(\d{3,4})p$/.exec(simpleToken)
    if (pMatch) {
      const vertical = Number.parseInt(pMatch[1], 10)
      if (Number.isFinite(vertical)) {
        if (vertical >= 4000) {
          bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES['4k'])
        } else if (vertical >= 3000) {
          bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES['3k'])
        } else if (vertical >= 2000) {
          bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES['2k'])
        } else if (vertical >= 1440) {
          bestScore = Math.max(bestScore, 2560 * 1440)
        } else if (vertical >= 1080) {
          bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES['hd'])
        } else if (vertical >= 720) {
          bestScore = Math.max(bestScore, 1280 * 720)
        } else if (vertical >= 480) {
          bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES['sd'])
        } else {
          bestScore = Math.max(bestScore, RESOLUTION_TOKEN_SCORES['lq'])
        }
      }
      continue
    }
  }

  return bestScore
}

export const getResolutionOptionScore = (option?: { value?: string; label?: string }): number => {
  if (!option) {
    return 0
  }

  const candidates = [option.value, option.label]

  let best = 0
  for (const candidate of candidates) {
    best = Math.max(best, getResolutionScore(candidate))
  }
  return best
}

export const sortResolutionOptions = <T extends { value?: string; label?: string }>(options: T[]): T[] => {
  return options.slice().sort((a, b) => getResolutionOptionScore(b) - getResolutionOptionScore(a))
}
